import { usePostHog as usePostHogHook } from "posthog-js/react";
import { isInjectedWebKitBridgeException } from "shared/utils/isInjectedWebKitBridgeException";

type PostHogWidgetProperties = Partial<
  Record<string, string | number>
>;

// PostHog is a page-wide singleton. A single page can mount multiple widgets
// (e.g. several Tripper embeds), so init must happen exactly once per page.
// Re-initializing is a no-op in posthog-js, and crucially we must NOT call
// reset()/opt_out_capturing() on subsequent mounts — doing so disables
// capturing for the whole page. This module-level flag is shared across all
// widget instances because the embed bundle is loaded once.
let isInitialized = false;

/**
 * Lightweight PostHog hook for embeddable widgets.
 * Avoids importing Auth0, app contexts, and gql/generated
 * to keep the widget bundle minimal.
 */
export const usePostHogWidget = () => {
  const posthog = usePostHogHook();

  const initPostHog = (name: string, persistence_name?: string) => {
    if (isInitialized) return;
    isInitialized = true;

    if (["development", "production"].includes(import.meta.env.VITE_ENVIRONMENT ?? "")) {
      posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        name,
        persistence_name: persistence_name ?? name,
        defaults: "2025-11-30",
        disable_session_recording: true,
        opt_out_capturing_by_default: false,
        before_send: event =>
          isInjectedWebKitBridgeException(event) ? null : event,
      });
    }
  };

  const registerPostHog = (properties: PostHogWidgetProperties) => {
    posthog.register(properties);
  };

  const trackPostHogEvent = (
    event: string,
    properties?: PostHogWidgetProperties,
  ) => {
    posthog.capture(event, properties);
  };

  return { posthog, initPostHog, registerPostHog, trackPostHogEvent };
};
